import { Injectable } from '@angular/core';
import { LinkUtilService } from './link-util.service';

@Injectable({
  providedIn: 'root',
})
export class QuillLoaderService {
  private quillLoaded = false;
  private quillPromise: Promise<any> | null = null;

  constructor(private linkUtilService: LinkUtilService) {}

  async loadQuill(): Promise<any> {
    if (this.quillLoaded) {
      return (window as any).Quill;
    }

    if (this.quillPromise) {
      return this.quillPromise;
    }

    this.quillPromise = this.dynamicLoadQuill();
    return this.quillPromise;
  }

  private async dynamicLoadQuill(): Promise<any> {
    try {
      // Check if Quill CSS is already loaded (from angular.json or previous load)
      const hasQuillCSS =
        document.querySelector('link[href*="quill.core.css"]') ||
        document.querySelector('link[href*="quill.snow.css"]') ||
        document.querySelector('style')?.textContent?.includes('.ql-toolbar');

      // Only load CSS dynamically if not already present
      if (!hasQuillCSS) {
        await this.loadCSS('https://cdn.quilljs.com/1.3.7/quill.core.css');
        await this.loadCSS('https://cdn.quilljs.com/1.3.7/quill.snow.css');
      }

      // Load Quill JS
      const Quill = await import('quill');
      (window as any).Quill = Quill.default || Quill;

      this.quillLoaded = true;
      return (window as any).Quill;
    } catch (error) {
      console.error('Failed to load Quill:', error);
      throw error;
    }
  }

  private loadCSS(href: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`link[href="${href}"]`)) {
        resolve();
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = () => resolve();
      link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`));
      document.head.appendChild(link);
    });
  }

  /**
   * DOM 노드에 대응하는 Quill blot을 돌려준다. Quill이 detach한 노드(내용 재렌더 등)면 null.
   * `Quill.find`는 static이라 인스턴스로는 못 부른다 — Quill을 소유한 이 서비스가 대신 노출한다.
   */
  findBlot(node: Node): any {
    return (window as any).Quill?.find(node) ?? null;
  }

  async createQuillInstance(container: HTMLElement, options: any = {}): Promise<any> {
    const Quill = await this.loadQuill();

    // Register custom Link format to handle internal/external links differently
    this.registerCustomLinkFormat(Quill);

    const defaultOptions = {
      theme: 'snow',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ header: 1 }, { header: 2 }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ direction: 'rtl' }],
          [{ size: ['small', false, 'large', 'huge'] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ color: [] }, { background: [] }],
          [{ font: [] }],
          [{ align: [] }],
          ['clean'],
          ['link', 'image'],
        ],
      },
      placeholder: '내용을 입력하세요...',
      readOnly: false,
    };

    const mergedOptions = { ...defaultOptions, ...options };
    return new Quill(container, mergedOptions);
  }

  private registerCustomLinkFormat(Quill: any): void {
    // Check if already registered to avoid duplicate registration
    if ((Quill as any).__customLinkRegistered) {
      return;
    }

    const Link = Quill.import('formats/link');
    // Capture linkUtilService in closure for use in static method
    const linkUtilService = this.linkUtilService;

    class CustomLink extends Link {
      static create(value: string) {
        const node = super.create(value);
        value = this.sanitize(value);
        node.setAttribute('href', value);
        node.setAttribute('target', '_blank');

        // Use LinkUtilService to determine if link is internal or external
        const isInternal = linkUtilService.isInternalLink(value);

        // Internal links: only noopener (preserve referrer for analytics)
        // External links: noopener noreferrer (security)
        if (isInternal) {
          node.setAttribute('rel', 'noopener');
        } else {
          node.setAttribute('rel', 'noopener noreferrer');
        }

        return node;
      }
    }

    Quill.register(CustomLink, true);
    (Quill as any).__customLinkRegistered = true;
  }
}
